/* eslint-disable unicorn/filename-case */
/* eslint-disable-next-line unicorn/filename-case */
/* eslint-disable prefer-const */
/* eslint-disable prefer-destructuring */
/* eslint-disable no-console */
/* eslint-disable import/no-cycle */

import axios, { AxiosRequestConfig, AxiosResponse, Method } from "axios";

import { getAuthToken } from "../utils/storage";
import { refreshToken } from "../utils/token";
import { ApiService } from "./ApiService";
import Keys from "./Keys";

const AUTHORIZATION_TYPES = {
  BASIC: "Basic",
  BEARER: "Bearer",
} as const;

const encodeBase64DivID = (): string | null => {
  try {
    return btoa(Keys.DivisionID);
  } catch (error) {
    console.log("encode error", error);
    return null;
  }
};

const getAuthenticatedHeader = (
  authorizationType: string,
  token: string | null,
  needBinusAuthorization: boolean = false
) => {
  const headers = {
    "Content-Type": "application/json",
    Authorization: "",
    BinusAuthorization: "",
  };

  const base64DivID = encodeBase64DivID();

  if (authorizationType === AUTHORIZATION_TYPES.BASIC && base64DivID) {
    headers.Authorization = `${authorizationType} ${base64DivID}`;
  } else if (authorizationType === AUTHORIZATION_TYPES.BEARER) {
    headers.Authorization = `${authorizationType} ${token}`;

    if (needBinusAuthorization && base64DivID) {
      headers.BinusAuthorization = `${authorizationType} ${base64DivID}`;
    }
  }

  return headers;
};

const getSignedRequestOptions = (
  method: Method,
  data: unknown,
  authorizationType: string,
  token: string | null,
  needBinusAuthorization: boolean = false
): AxiosRequestConfig => {
  const headers = getAuthenticatedHeader(
    authorizationType,
    token,
    needBinusAuthorization
  );
  return { method, data, headers };
};

const doFetch = async (url: string, requestOptions: AxiosRequestConfig) =>
  axios(url, requestOptions)
    .then((result) => result)
    .catch(async (error) => {
      let errorMessage =
        error.response && error.response.data
          ? error.response.data
          : error.message;
      errorMessage = errorMessage || JSON.stringify(error);

      logError(error);
      throw new Error(errorMessage.errorMessage);
    });

const logError = async (error: any) => {
  try {
    const logErrorUrl = ApiService.logError.insert;
    const currentUrl = window.location.href;
    const lastUrlSegment = currentUrl.split("/").pop() || "";
    const userContext = lastUrlSegment.startsWith("RQ") ? lastUrlSegment : null;

    const requestParams = {
      requestUrl: error.config?.url,
      requestMethod: error.config?.method,
      requestData: error.config?.data,
    };

    const errorData = {
      ErrorType: "Frontend",
      UserContext: userContext,
      UrlPage: currentUrl,
      Params: JSON.stringify(requestParams),
      ExceptionDetail: error.message,
      StackTrace: error.stack,
    };

    const requestOptions = getSignedRequestOptions(
      "POST",
      errorData,
      AUTHORIZATION_TYPES.BASIC,
      null
    );

    await axios(logErrorUrl, requestOptions);
  } catch (loggingError) {
    console.error("Failed to log error:", loggingError);
  }
};

const getValidAuthToken = async (): Promise<string> => {
  const token = getAuthToken();
  if (!token || (token.value && token.isExpired && token.isBypass)) {
    window.location.href = "/logout";
    throw new Error("Token is not available");
  }
  if (token.value && !token.isExpired) return token.value;
  if (token.value && token.isExpired) {
    const newToken = await refreshToken();
    if (newToken) return newToken;
    throw new Error("Failed to refresh token");
  }
  throw new Error("Token is not available or expired");
};

export const HttpService = {
  getAuthorizationToken<T = any>(): Promise<AxiosResponse<T>> {
    const requestOptions = getSignedRequestOptions(
      "GET",
      null,
      AUTHORIZATION_TYPES.BASIC,
      null
    );
    return doFetch(ApiService.auth.getToken, requestOptions);
  },

  getAuthorizationTokenMsal<T = any>(
    azureADToken: string
  ): Promise<AxiosResponse<T>> {
    const requestOptions = getSignedRequestOptions(
      "GET",
      null,
      AUTHORIZATION_TYPES.BEARER,
      azureADToken,
      true
    );
    return doFetch(ApiService.auth.getTokenMsal, requestOptions);
  },

  async getAuthorizationTokenBypass<T = any>(
    email: string,
    password: string
  ): Promise<AxiosResponse<T>> {
    try {
      const token = await getValidAuthToken();
      const requestOptions = getSignedRequestOptions(
        "POST",
        { email, password },
        AUTHORIZATION_TYPES.BEARER,
        token,
        true
      );
      return await doFetch(ApiService.auth.getTokenBypass, requestOptions);
    } catch (error) {
      console.error("Failed to get valid token:", error);
      throw error;
    }
  },

  async get<T = any>(url: string): Promise<AxiosResponse<T>> {
    try {
      const token = await getValidAuthToken();
      const requestOptions = getSignedRequestOptions(
        "GET",
        null,
        AUTHORIZATION_TYPES.BEARER,
        token
      );
      return await doFetch(url, requestOptions);
    } catch (error) {
      console.error("Failed to get valid token:", error);
      throw error;
    }
  },

  async post<T = any>(url: string, data: unknown): Promise<AxiosResponse<T>> {
    try {
      const token = await getValidAuthToken();
      const requestOptions = getSignedRequestOptions(
        "POST",
        data,
        AUTHORIZATION_TYPES.BEARER,
        token
      );
      return await doFetch(url, requestOptions);
    } catch (error) {
      console.error("Failed to get valid token:", error);
      throw error;
    }
  },
};
